import { IMPORT_SLICE } from '@itc/shared';
import type { Agreement, LinkState, OrgStatus } from './rules.ts';
import { MAX_BIO } from './sync.ts';

// Importing ITCBot's `members` and `organizations` (plan 5B Chunk 7, R7, ADR 0019), as pure
// functions: what one old row becomes, and what a slice of them does against what is
// already stored. The server reads, writes and audits; these decide.
//
// A row is skipped if its id is already stored, refused if it can't be imported or clashes
// with another row, and created otherwise. A refusal says which key clashed and the other
// row's id: never a handle, a Discord id or a name, so a report can go in a document.

export const RECORD_ID = /^[a-z0-9]{15}$/;
const DISCORD_ID = /^[0-9]{1,20}$/;

/** What each field of `members_profiles` and `members_orgs` holds at most, from their migrations. */
export const LIMITS = {
  handle: 64,
  biocode: 32,
  banReason: 1000,
  symbol: 32,
  orgName: 200,
  tag: 64,
  notes: 2000,
  json: { roles: 8192, currentOrgs: 65536, history: 32768, reasons: 16384, hostile: 8192, outreach: 65536, activities: 4096 },
} as const;

/** The request budget for a slice, under the server's 1 MiB body limit with room for the rest of the request. */
export const SLICE_CHARACTERS = 700_000;

// --- Reading one old row ------------------------------------------------------------------

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');
const flag = (value: unknown): boolean => value === true;
const strings = (value: unknown): string[] => (Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string' && item.trim() !== '') : []);
const placeholder = (value: string): boolean => value === '' || value.toUpperCase() === 'N/A';
const bytes = (value: unknown): number => (value === undefined ? 0 : new TextEncoder().encode(JSON.stringify(value)).length);
const unique = (items: readonly string[]): string[] => [...new Set(items)];

/** ITCBot's `rsi_itc_member_type`, to ours. Blank is a row that was never read. */
const MEMBER_TYPES: Readonly<Record<string, OrgStatus>> = { Member: 'member', Affiliate: 'affiliate', Pending: 'pending', 'N/A': 'none', '': 'none' };

/** An old row's date, or null. */
export function readDate(created: string): Date | null {
  const at = new Date(created.replace(' ', 'T'));
  return Number.isNaN(at.getTime()) ? null : at;
}

/** A row of ITCBot's `members`, as the browser sends it. Everything but the id and the date is as the old instance had it. */
export interface RawMember {
  id: string;
  created: string;
  discord_id?: unknown;
  rsi_handle?: unknown;
  biocode?: unknown;
  rsi_bio?: unknown;
  linked?: unknown;
  rsi_itc_hidden?: unknown;
  rsi_itc_member_type?: unknown;
  rsi_itc_member_roles?: unknown;
  rsi_current_orgs?: unknown;
  rsi_org_history?: unknown;
  rsi_redacted_org_count?: unknown;
  evasive?: unknown;
  antipiracy?: unknown;
  onboard_complete?: unknown;
  outreach_data?: unknown;
  banned?: unknown;
  threat_reasons?: unknown;
  hostile_orgs_found?: unknown;
  ban_reason?: unknown;
}

/** What a created profile holds, besides its id and `source`. */
export interface ProfileFields {
  discord_id: string;
  rsi_handle: string;
  handle_key: string;
  biocode: string;
  link_state: LinkState;
  org_status: OrgStatus;
  rsi_roles: string[];
  rsi_visibility: 'Redacted' | 'Visible';
  current_orgs: { symbol: string; name: string; activities: string[] }[];
  org_history: string[];
  redacted_org_count: number;
  bio: string;
  is_threat: boolean;
  threat_reasons: string[];
  hostile_orgs: string[];
  permanent_threat: boolean;
  ban_reason: string;
  evasive: boolean;
  agreement: Agreement;
  onboarded: boolean;
  outreach?: unknown;
}

/** A key a row holds that no other may: `kind` names it in a refusal. */
export interface Key {
  kind: string;
  value: string;
}

export interface Candidate<Fields> {
  id: string;
  oldCreated: Date;
  fields: Fields;
  keys: Key[];
}

export type Read<Fields> = { ok: true; candidate: Candidate<Fields> } | { ok: false; reason: string };

const refuse = (reason: string): { ok: false; reason: string } => ({ ok: false, reason });

/** The unique keys of a row, as the plan compares them. Null when its id, date or identity can't be read. */
export function memberKeys(raw: RawMember): Key[] | null {
  const read = readMember(raw);
  return read.ok ? read.candidate.keys : null;
}

/**
 * R7's field map for one `members` row.
 *
 * - `linked` becomes `pre_migration`, with `biocode` kept and `linked_at` left blank: nothing
 *   here proved it.
 * - `banned` becomes `permanent_threat`, and `is_threat` is true then, and for any
 *   `threat_reasons`.
 * - `rsi_itc_member_type` (Member, Affiliate, Pending, N/A) becomes `org_status`. Another
 *   value is refused rather than guessed, and not repeated in the refusal.
 * - `rsi_itc_hidden` becomes `rsi_visibility`. `rsi_current_orgs` are symbols only, so they
 *   have no names or activities. `antipiracy` becomes `agreement: agreed`.
 * - `outreach_data` goes across as it is, unread until 5D.
 * - A Discord id with a blank or `N/A` handle imports with no handle. Neither is refused.
 */
export function readMember(raw: RawMember): Read<ProfileFields> {
  if (!RECORD_ID.test(raw.id)) return refuse('Its id isn’t a record id');
  const oldCreated = readDate(raw.created);
  if (oldCreated === null) return refuse('Its date can’t be read');

  const rawHandle = text(raw.rsi_handle);
  const handle = placeholder(rawHandle) ? '' : rawHandle;
  const rawDiscord = typeof raw.discord_id === 'number' && Number.isInteger(raw.discord_id) ? String(raw.discord_id) : text(raw.discord_id);
  const discordId = placeholder(rawDiscord) ? '' : rawDiscord;
  const rawCode = text(raw.biocode);
  const biocode = placeholder(rawCode) ? '' : rawCode;

  if (handle === '' && discordId === '') return refuse('It has neither an RSI handle nor a Discord id');
  if (discordId !== '' && !DISCORD_ID.test(discordId)) return refuse('Its Discord id isn’t a Discord id');
  if (handle.length > LIMITS.handle) return refuse(`Its handle is longer than ${String(LIMITS.handle)} characters`);
  if (biocode.length > LIMITS.biocode) return refuse(`Its biocode is longer than ${String(LIMITS.biocode)} characters`);

  const type = MEMBER_TYPES[text(raw.rsi_itc_member_type)];
  if (type === undefined) return refuse('Its member type isn’t Member, Affiliate, Pending or N/A');

  const banReason = typeof raw.ban_reason === 'string' ? raw.ban_reason : '';
  if (banReason.length > LIMITS.banReason) return refuse(`Its ban reason is longer than ${String(LIMITS.banReason)} characters`);

  const reasons = strings(raw.threat_reasons);
  const banned = flag(raw.banned);
  const roles = typeof raw.rsi_itc_member_roles === 'string' ? strings([raw.rsi_itc_member_roles]) : strings(raw.rsi_itc_member_roles);
  const current = unique(strings(raw.rsi_current_orgs).map((symbol) => symbol.trim().toUpperCase())).map((symbol) => ({ symbol, name: '', activities: [] as string[] }));
  const history = unique(strings(raw.rsi_org_history).map((symbol) => symbol.trim().toUpperCase()));
  const hostile = strings(raw.hostile_orgs_found);
  const count = typeof raw.rsi_redacted_org_count === 'number' && Number.isFinite(raw.rsi_redacted_org_count) ? Math.max(0, Math.floor(raw.rsi_redacted_org_count)) : 0;
  const outreach = raw.outreach_data === undefined || raw.outreach_data === null ? undefined : raw.outreach_data;

  const sizes: [string, unknown, number][] = [
    ['roles', roles, LIMITS.json.roles],
    ['current orgs', current, LIMITS.json.currentOrgs],
    ['org history', history, LIMITS.json.history],
    ['threat reasons', reasons, LIMITS.json.reasons],
    ['hostile orgs', hostile, LIMITS.json.hostile],
    ['outreach data', outreach, LIMITS.json.outreach],
  ];
  for (const [name, value, limit] of sizes) if (bytes(value) > limit) return refuse(`Its ${name} are too large to keep`);

  const keys: Key[] = [];
  if (handle !== '') keys.push({ kind: 'handle', value: handle.toLowerCase() });
  if (discordId !== '') keys.push({ kind: 'Discord id', value: discordId });
  if (biocode !== '') keys.push({ kind: 'biocode', value: biocode });

  const fields: ProfileFields = {
    discord_id: discordId,
    rsi_handle: handle,
    handle_key: handle.toLowerCase(),
    biocode,
    link_state: flag(raw.linked) ? 'pre_migration' : '',
    org_status: type,
    rsi_roles: roles,
    rsi_visibility: flag(raw.rsi_itc_hidden) ? 'Redacted' : 'Visible',
    current_orgs: current,
    org_history: history,
    redacted_org_count: count,
    bio: typeof raw.rsi_bio === 'string' ? raw.rsi_bio.slice(0, MAX_BIO) : '',
    is_threat: banned || reasons.length > 0,
    threat_reasons: reasons,
    hostile_orgs: hostile,
    permanent_threat: banned,
    ban_reason: banReason,
    evasive: flag(raw.evasive),
    agreement: flag(raw.antipiracy) ? 'agreed' : '',
    onboarded: flag(raw.onboard_complete),
    ...(outreach === undefined ? {} : { outreach }),
  };
  return { ok: true, candidate: { id: raw.id, oldCreated, fields, keys } };
}

// --- ITCBot's organizations -----------------------------------------------------------------

/** A row of ITCBot's `organizations`. */
export interface RawOrg {
  id: string;
  created: string;
  rsi_org_symbol?: unknown;
  rsi_org_activities?: unknown;
  tag?: unknown;
  notes?: unknown;
  name?: unknown;
}

export interface OrgFields {
  symbol: string;
  name: string;
  activities: string[];
  tag: string;
  notes: string;
}

/** `rsi_org_symbol` becomes `symbol`, upper-case and unique. A row with none is refused. */
export function readOrg(raw: RawOrg): Read<OrgFields> {
  if (!RECORD_ID.test(raw.id)) return refuse('Its id isn’t a record id');
  const oldCreated = readDate(raw.created);
  if (oldCreated === null) return refuse('Its date can’t be read');
  const symbol = text(raw.rsi_org_symbol).toUpperCase();
  if (symbol === '') return refuse('It has no symbol');
  if (symbol.length > LIMITS.symbol) return refuse(`Its symbol is longer than ${String(LIMITS.symbol)} characters`);
  const name = typeof raw.name === 'string' ? raw.name : '';
  const tag = typeof raw.tag === 'string' ? raw.tag : '';
  const notes = typeof raw.notes === 'string' ? raw.notes : '';
  const activities = strings(raw.rsi_org_activities);
  if (name.length > LIMITS.orgName) return refuse(`Its name is longer than ${String(LIMITS.orgName)} characters`);
  if (tag.length > LIMITS.tag) return refuse(`Its tag is longer than ${String(LIMITS.tag)} characters`);
  if (notes.length > LIMITS.notes) return refuse(`Its notes are longer than ${String(LIMITS.notes)} characters`);
  if (bytes(activities) > LIMITS.json.activities) return refuse('Its activities are too large to keep');
  return { ok: true, candidate: { id: raw.id, oldCreated, fields: { symbol, name, activities, tag, notes }, keys: [{ kind: 'symbol', value: symbol }] } };
}

export function orgKeys(raw: RawOrg): Key[] | null {
  const read = readOrg(raw);
  return read.ok ? read.candidate.keys : null;
}

// --- A slice against what is stored ---------------------------------------------------------

/** Ids and unique keys already taken, each key with the id of the row that holds it. */
export interface Taken {
  ids: Set<string>;
  keys: Map<string, string>;
}

export const emptyTaken = (): Taken => ({ ids: new Set(), keys: new Map() });

const keyOf = (key: Key): string => `${key.kind}\u0000${key.value}`;

/** One row that would take the keys it holds, or has. */
export interface Claim {
  id: string;
  keys: Key[];
}

/** Adds a claim to `taken`: an earlier slice's row that would be created. */
export function take(taken: Taken, claim: Claim): void {
  taken.ids.add(claim.id);
  for (const key of claim.keys) if (!taken.keys.has(keyOf(key))) taken.keys.set(keyOf(key), claim.id);
}

export interface Refusal {
  id: string;
  reason: string;
}

export interface Plan<Fields> {
  create: Candidate<Fields>[];
  skipped: number;
  refused: Refusal[];
}

/**
 * R7's table. In order, for each row: an id that isn't a record id, or a date that can't be
 * read, is refused; an id already stored (or earlier in this slice) is skipped, which is
 * what makes a repeat safe; a row that can't be read is refused; a row whose handle,
 * Discord id or biocode (or, for an organisation, symbol) another row holds, stored or
 * earlier in the slice, is refused saying which and the other row's id; otherwise it's
 * created. `taken` is what is stored, plus, in a dry run, what earlier slices would create.
 * It isn't changed.
 */
export function planImport<Raw extends { id: string }, Fields>(rows: readonly Raw[], read: (raw: Raw) => Read<Fields>, taken: Taken): Plan<Fields> {
  const plan: Plan<Fields> = { create: [], skipped: 0, refused: [] };
  const seen: Taken = { ids: new Set(taken.ids), keys: new Map(taken.keys) };
  for (const raw of rows) {
    // A row stored under this id is a repeat, however it reads now.
    if (seen.ids.has(raw.id)) {
      plan.skipped += 1;
      continue;
    }
    const result = read(raw);
    if (!result.ok) {
      plan.refused.push({ id: raw.id, reason: result.reason });
      continue;
    }
    const { candidate } = result;
    const clash = candidate.keys.find((key) => seen.keys.has(keyOf(key)));
    if (clash !== undefined) {
      plan.refused.push({ id: raw.id, reason: `Its ${clash.kind} clashes with row ${seen.keys.get(keyOf(clash)) ?? ''}` });
      continue;
    }
    take(seen, candidate);
    plan.create.push(candidate);
  }
  return plan;
}

export const planMembers = (rows: readonly RawMember[], taken: Taken): Plan<ProfileFields> => planImport(rows, readMember, taken);
export const planOrgs = (rows: readonly RawOrg[], taken: Taken): Plan<OrgFields> => planImport(rows, readOrg, taken);

// --- What a dry run carries from one slice to the next --------------------------------------

/**
 * A dry run writes nothing, so a later slice can't see an earlier one's rows. The browser
 * remembers which rows the server said it would create, and sends a slice only the claims
 * that share an id or a key with its own rows, so the dry run finds the same clashes the
 * import will. The server trusts them for a dry run only: an import reads what is stored.
 */
export class Claims {
  readonly #byKey = new Map<string, Claim>();

  add(claim: Claim): void {
    this.#byKey.set(`id\u0000${claim.id}`, claim);
    for (const key of claim.keys) if (!this.#byKey.has(keyOf(key))) this.#byKey.set(keyOf(key), claim);
  }

  /** The claims this slice's rows could clash with. `keys` is null for a row that can't be read. */
  for(rows: readonly { id: string; keys: readonly Key[] | null }[]): Claim[] {
    const found = new Map<string, Claim>();
    for (const row of rows) {
      const hits = [this.#byKey.get(`id\u0000${row.id}`), ...(row.keys ?? []).map((key) => this.#byKey.get(keyOf(key)))];
      for (const hit of hits) if (hit !== undefined) found.set(hit.id, hit);
    }
    return [...found.values()];
  }
}

// --- Slicing a bundle for the browser ------------------------------------------------------

/**
 * Splits rows into requests of at most `IMPORT_SLICE` rows and `SLICE_CHARACTERS`
 * characters, in order. A row too large for any request goes alone.
 */
export function sliceRows<T>(rows: readonly T[], limit: number = IMPORT_SLICE, characters: number = SLICE_CHARACTERS): T[][] {
  const slices: T[][] = [];
  let current: T[] = [];
  let size = 0;
  for (const row of rows) {
    const length = JSON.stringify(row).length;
    if (current.length > 0 && (current.length >= limit || size + length > characters)) {
      slices.push(current);
      current = [];
      size = 0;
    }
    current.push(row);
    size += length;
  }
  if (current.length > 0) slices.push(current);
  return slices;
}
